<template>
  <div class="orders-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>订单管理</h2>
        <p class="subtitle">查看和管理所有支付订单</p>
      </div>
      <div class="header-stats">
        <span class="stat-item">
          <span class="stat-num">{{ stats.total }}</span>
          <span class="stat-label">总订单</span>
        </span>
        <span class="stat-item">
          <span class="stat-num ok">{{ stats.paid }}</span>
          <span class="stat-label">已支付</span>
        </span>
        <span class="stat-item">
          <span class="stat-num warn">{{ stats.pending }}</span>
          <span class="stat-label">待执行</span>
        </span>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="search-box">
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
        </svg>
        <input v-model="search" placeholder="搜索订单号 / 商品名..." />
      </div>
      <div class="filter-group">
        <select v-model="statusFilter">
          <option value="">全部状态</option>
          <option value="paid">已支付</option>
          <option value="unpaid">未支付</option>
          <option value="closed">已关闭</option>
          <option value="refunded">已退款</option>
        </select>
        <select v-model="execFilter">
          <option value="">全部执行</option>
          <option value="pending">待执行</option>
          <option value="running">运行中</option>
          <option value="success">成功</option>
          <option value="failed">失败</option>
        </select>
        <button class="btn btn-primary" @click="fetchOrders" :disabled="loading">
          <svg v-if="!loading" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
            <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
          </svg>
          <svg v-else class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
            <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          {{ loading ? '刷新中...' : '刷新' }}
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="state-box loading">
      <div class="spinner"></div>
      <span>加载订单数据中...</span>
    </div>

    <!-- 空状态 -->
    <div v-else-if="filteredOrders.length === 0" class="state-box empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
        <line x1="9" y1="12" x2="15" y2="12"/>
        <line x1="9" y1="16" x2="15" y2="16"/>
      </svg>
      <p>暂无订单数据</p>
      <span>当前筛选条件下没有找到订单</span>
    </div>

    <!-- 数据表格 -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>订单号</th>
            <th>商品名称</th>
            <th>金额</th>
            <th>客户机器码</th>
            <th>支付状态</th>
            <th>执行状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in paginatedOrders" :key="o.out_trade_no">
            <td>
              <code class="order-id">{{ o.out_trade_no }}</code>
            </td>
            <td>{{ o.subject || '-' }}</td>
            <td class="amount">&yen;{{ Number(o.total_amount).toFixed(2) }}</td>
            <td class="mono">
              {{ (o.client_machine_id || '-').slice(0, 12) }}
              {{ o.client_machine_id && o.client_machine_id.length > 12 ? '...' : '' }}
            </td>
            <td>
              <span :class="['badge', statusBadge(o.status)]">
                {{ statusLabel(o.status) }}
              </span>
            </td>
            <td>
              <span :class="['badge exec-badge', execBadge(o.exec_status)]">
                {{ execLabel(o.exec_status) }}
              </span>
            </td>
            <td class="time-col">{{ formatTime(o.gmt_create || o.created_at) }}</td>
            <td>
              <button class="btn-link" @click="viewDetail(o)">详情</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="pagination">
        <button :disabled="page <= 1" @click="page--">&lsaquo;</button>
        <span class="pg-info">
          第 <b>{{ page }}</b> 页 / 共 <b>{{ totalPages }}</b> 页
          <span class="pg-total">（共 {{ filteredOrders.length }} 条）</span>
        </span>
        <button :disabled="page >= totalPages" @click="page++">&rsaquo;</button>
      </div>
    </div>

    <!-- 订单详情弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showDetail" class="modal-overlay" @click.self="showDetail = false">
          <div class="modal-box">
            <div class="modal-head">
              <h3>订单详情</h3>
              <button class="modal-close" @click="showDetail = false">&times;</button>
            </div>
            <div class="modal-body" v-if="detailOrder">
              <div class="detail-grid">
                <div class="dg-row">
                  <span class="dg-k">商户订单号</span>
                  <span class="dg-v mono">{{ detailOrder.out_trade_no }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付宝交易号</span>
                  <span class="dg-v">{{ detailOrder.trade_no || '-' }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">商品名称</span>
                  <span class="dg-v strong">{{ detailOrder.subject }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付金额</span>
                  <span class="dg-v amount-lg">&yen;{{ detailOrder.total_amount }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付状态</span>
                  <span class="dg-v">
                    <span :class="['badge', statusBadge(detailOrder.status)]">
                      {{ statusLabel(detailOrder.status) }}
                    </span>
                  </span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">执行状态</span>
                  <span class="dg-v">
                    <span :class="['badge exec-badge', execBadge(detailOrder.exec_status)]">
                      {{ execLabel(detailOrder.exec_status) }}
                    </span>
                  </span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">客户机器码</span>
                  <span class="dg-v mono">{{ detailOrder.client_machine_id || '-' }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">创建时间</span>
                  <span class="dg-v">{{ formatTime(detailOrder.gmt_create || detailOrder.created_at) }}</span>
                </div>
                <div class="dg-row" v-if="detailOrder.gmt_payment">
                  <span class="dg-k">支付时间</span>
                  <span class="dg-v">{{ formatTime(detailOrder.gmt_payment) }}</span>
                </div>
              </div>
            </div>
            <div class="modal-foot">
              <button class="btn btn-secondary" @click="showDetail = false">关闭</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const API_BASE = import.meta.env.VITE_API_BASE || ''

// 状态
const orders = ref([])
const loading = ref(false)
const search = ref('')
const statusFilter = ref('')
const execFilter = ref('')
const page = ref(1)
const pageSize = 20

// 详情弹窗
const showDetail = ref(false)
const detailOrder = ref(null)

// 计算属性：统计数据
const stats = computed(() => {
  return {
    total: orders.value.length,
    paid: orders.value.filter(o => o.status === 'paid').length,
    pending: orders.value.filter(o => o.exec_status === 'pending' && o.status === 'paid').length
  }
})

// 计算属性：筛选后的订单
const filteredOrders = computed(() => {
  let result = orders.value
  
  // 搜索筛选
  if (search.value) {
    const keyword = search.value.toLowerCase()
    result = result.filter(o => 
      (o.out_trade_no || '').toLowerCase().includes(keyword) ||
      (o.subject || '').toLowerCase().includes(keyword)
    )
  }
  
  // 状态筛选
  if (statusFilter.value) {
    result = result.filter(o => o.status === statusFilter.value)
  }
  
  // 执行状态筛选
  if (execFilter.value) {
    result = result.filter(o => o.exec_status === execFilter.value)
  }
  
  return result
})

// 计算属性：分页后的订单
const paginatedOrders = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredOrders.value.slice(start, start + pageSize)
})

// 计算属性：总页数
const totalPages = computed(() => {
  return Math.ceil(filteredOrders.value.length / pageSize)
})

// 获取订单列表
async function fetchOrders() {
  loading.value = true
  try {
    const token = localStorage.getItem('admin_token')
    const res = await fetch(`${API_BASE}/api/admin/orders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (data.success) {
      orders.value = data.orders || []
      page.value = 1 // 重置到第一页
    }
  } catch (e) {
    console.error('[Orders] Fetch error:', e)
    alert('获取订单失败: ' + e.message)
  } finally {
    loading.value = false
  }
}

// 查看详情
function viewDetail(order) {
  detailOrder.value = order
  showDetail.value = true
}

// 状态标签
function statusLabel(s) {
  return { unpaid: '未支付', paid: '已支付', closed: '已关闭', refunded: '已退款', expired: '已过期' }[s] || s || '-'
}

function statusBadge(s) {
  return { paid: 'ok', unpaid: 'warn', closed: 'muted', refunded: 'err', expired: 'muted' }[s] || 'muted'
}

function execLabel(s) {
  return { pending: '待执行', running: '运行中', success: '成功', failed: '失败' }[s] || s || '-'
}

function execBadge(s) {
  return { success: 'ok', pending: 'warn', running: 'running', failed: 'err' }[s] || 'muted'
}

function formatTime(t) {
  if (!t) return '-'
  try { return new Date(t).toLocaleString('zh-CN') } catch { return t }
}

onMounted(() => {
  fetchOrders()
})
</script>

<style scoped>
.orders-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
}

.header-left h2 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text, #111827);
  margin: 0 0 4px;
}

.subtitle {
  font-size: 14px;
  color: var(--text-2, #6b7280);
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  text-align: center;
}

.stat-num {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--text, #111827);
}

.stat-num.ok { color: #059669; }
.stat-num.warn { color: #d97706; }

.stat-label {
  font-size: 12px;
  color: var(--text-3, #9ca3af);
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 10px;
  padding: 0 12px;
  min-width: 280px;
  transition: all 0.2s;
}

.search-box:focus-within {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
}

.search-box svg {
  width: 17px;
  height: 17px;
  color: var(--text-3, #9ca3af);
}

.search-box input {
  border: none;
  background: none;
  padding: 10px 0;
  font-size: 14px;
  color: var(--text, #111827);
  outline: none;
  width: 100%;
}

.filter-group {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.filter-group select {
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid var(--border, #e5e7eb);
  background: var(--surface, #fff);
  color: var(--text-2, #374151);
  font-size: 13px;
  outline: none;
  cursor: pointer;
}

.filter-group select:focus {
  border-color: #4f46e5;
}

/* 按钮 */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #4f46e5;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.btn-secondary {
  background: var(--bg, #f3f4f6);
  color: var(--text-2, #374151);
  border-color: var(--border, #e5e7eb);
}

.btn-secondary:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 状态框 */
.state-box {
  text-align: center;
  padding: 60px 0;
  color: var(--text-3, #9ca3af);
  font-size: 14px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
}

.state-box.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.state-box.empty svg {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  color: var(--text-4, #d1d5db);
}

.state-box.empty p {
  font-size: 16px;
  margin: 0 0 4px;
  color: var(--text-2, #6b7280);
}

.state-box.empty span {
  font-size: 13px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2.5px solid var(--border, #e5e7eb);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* 表格卡片 */
.table-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-3, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--bg, #f9fafb);
  border-bottom: 1px solid var(--border, #e5e7eb);
  white-space: nowrap;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
  vertical-align: middle;
}

.data-table tbody tr:hover td {
  background: rgba(79, 70, 229, 0.04);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.order-id {
  font-family: 'Cascadia Code', 'Fira Code', monospace;
  font-size: 12px;
  background: var(--bg, #f3f4f6);
  padding: 2px 8px;
  border-radius: 4px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

.mono {
  font-family: 'Cascadia Code', monospace;
  font-size: 12px;
  color: var(--text-2, #6b7280);
}

.amount {
  font-weight: 700;
  color: #059669;
}

.time-col {
  color: var(--text-3, #9ca3af);
  font-size: 12px;
  white-space: nowrap;
}

/* 徽章 */
.badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

.badge.ok { background: #ecfdf5; color: #059669; }
.badge.warn { background: #fffbeb; color: #d97706; }
.badge.err { background: #fef2f2; color: #dc2626; }
.badge.muted { background: #f9fafb; color: #6b7280; }
.badge.running { background: #eef2ff; color: #4f46e5; }

/* 链接按钮 */
.btn-link {
  background: none;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s;
}

.btn-link:hover {
  background: rgba(79, 70, 229, 0.08);
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid var(--border-light, #f3f4f6);
}

.pg-info {
  font-size: 13px;
  color: var(--text-2, #374151);
}

.pg-info b {
  color: var(--text, #111827);
}

.pg-total {
  color: var(--text-3, #9ca3af);
  margin-left: 8px;
}

.pagination button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  color: var(--text-2, #374151);
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.pagination button:disabled {
  opacity: 0.3;
  cursor: default;
}

.pagination button:not(:disabled):hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-box {
  background: var(--surface, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border, #e5e7eb);
}

.modal-head h3 {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--bg, #f3f4f6);
  color: var(--text-2, #6b7280);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.modal-close:hover {
  background: #fef2f2;
  color: #dc2626;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  max-height: 60vh;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border, #e5e7eb);
  background: var(--bg, #f9fafb);
}

/* 详情网格 */
.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dg-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  align-items: center;
}

.dg-k {
  font-size: 13px;
  color: var(--text-3, #9ca3af);
  font-weight: 500;
}

.dg-v {
  font-size: 14px;
  color: var(--text, #111827);
  word-break: break-all;
}

.dg-v.strong {
  font-weight: 600;
}

.dg-v.amount-lg {
  font-size: 18px;
  font-weight: 700;
  color: #059669;
}

/* 动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-box,
.modal-leave-active .modal-box {
  transition: transform 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-box,
.modal-leave-to .modal-box {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

/* 响应式 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-stats {
    width: 100%;
    justify-content: space-around;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: unset;
  }

  .filter-group {
    margin-left: 0;
  }

  .data-table {
    font-size: 12px;
  }

  .data-table th,
  .data-table td {
    padding: 8px 12px;
  }

  .dg-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .modal-box {
    max-width: 100%;
    margin: 10px;
  }
}
</style>
